import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	Output,
	ViewChild,
	ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConnectedPosition, Overlay, OverlayModule, ScrollStrategy } from '@angular/cdk/overlay';

import { E2EModule } from 'cypress/support/e2e.module';
import { DropdownOption } from 'src/app/shared/mine-dropdown/mine-dropdown.interface';
import { ContentPipeModule } from 'src/app/services/content/content-pipe.module';
import { MenuPositionEnum } from './mine-action-menu-enum';
import { Animations } from 'src/app/animations/animations';
import { TippyModule } from '../tippy.module';

@Component({
	selector: 'mine-action-menu',
	templateUrl: './mine-action-menu.component.html',
	styleUrls: ['./mine-action-menu.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [ContentPipeModule, CommonModule, TippyModule, E2EModule, OverlayModule],
	animations: [Animations.fadeInOut],
})
export class MineActionMenuComponent implements AfterViewInit {
	@Output()
	action = new EventEmitter<string>();

	@Input()
	options: DropdownOption[];

	@Input()
	position = MenuPositionEnum.Bottom;

	@Input()
	panelStyle: { [key: string]: string } = {};

	// z-index for option tooltips. The menu renders inside the cdk overlay container (z-index 10000),
	// so an option tooltip MUST sit above that or it paints behind the menu and is invisible. Default
	// above 10000; callers can override if their stacking differs.
	@Input()
	tooltipZIndex = 10001;

	@Output()
	menuOpened = new EventEmitter<void>();

	@Output()
	menuClosed = new EventEmitter<void>();

	@ViewChild('contentWrapper', { static: false })
	contentWrapper: ElementRef<HTMLElement>;

	showActionsMenu: boolean;

	hasNgContent: boolean = false;

	readonly scrollStrategy: ScrollStrategy;

	constructor(
		private cdr: ChangeDetectorRef,
		private overlay: Overlay,
	) {
		this.scrollStrategy = this.overlay.scrollStrategies.close();
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			if (this.contentWrapper?.nativeElement) {
				const hasContent =
					this.contentWrapper.nativeElement.children.length > 0 ||
					(this.contentWrapper.nativeElement.textContent?.trim().length ?? 0) > 0;
				this.hasNgContent = hasContent;
				this.cdr.detectChanges();
			}
		}, 0);
	}

	closeMenu(): void {
		if (!this.showActionsMenu) {
			return;
		}
		this.showActionsMenu = false;
		this.menuClosed.emit();
		this.cdr.markForCheck();
	}

	get overlayPositions(): ConnectedPosition[] {
		const openDown: ConnectedPosition = { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 5 };
		const openUp: ConnectedPosition = { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -5 };
		// Each primary gets the opposite as a fallback so CDK flips the menu instead of letting it
		// overflow the viewport edge and clip the last option (e.g. "Delete" on a low trigger).
		switch (this.position) {
			case MenuPositionEnum.Top:
				return [openUp, openDown];
			case MenuPositionEnum.Bottom:
			case MenuPositionEnum.BottomRight:
			default:
				return [openDown, openUp];
		}
	}

	menuClick($event: MouseEvent): void {
		this.showActionsMenu = !this.showActionsMenu;
		if (this.showActionsMenu) {
			this.menuOpened.emit();
		} else {
			this.menuClosed.emit();
		}
		this.stopEvent($event);
	}

	onActionClick(optionId: string, $event: MouseEvent): void {
		if (this.options.find((option) => option.id === optionId)?.disabled) return; // for disabled options
		this.action.emit(optionId);
		this.showActionsMenu = false;
		this.menuClosed.emit();
		this.stopEvent($event);
	}

	// when menu is over other button we need to stop events from bubbling up
	private stopEvent($event: MouseEvent): void {
		$event.stopPropagation();
		$event.preventDefault();
	}
}
